import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Steps, Button, Form, Input, Select, DatePicker, InputNumber, Radio, Switch,
  Card, Row, Col, Typography, Space, message, Divider, Tag, Result,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, SaveOutlined,
  HomeOutlined, FileTextOutlined, ToolOutlined, SettingOutlined,
  DollarOutlined, CalendarOutlined, ApartmentOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { KSGService } from '../api/services/ksg.service';

const { Title, Text, Paragraph } = Typography;

const STEPS = [
  { title: 'Земельный участок', icon: <HomeOutlined />, key: 'land' },
  { title: 'Параметры объекта', icon: <ApartmentOutlined />, key: 'object' },
  { title: 'Проектная документация', icon: <FileTextOutlined />, key: 'design' },
  { title: 'Инженерные сети', icon: <ToolOutlined />, key: 'engineering' },
  { title: 'Материалы и логистика', icon: <SettingOutlined />, key: 'materials' },
  { title: 'Организация строительства', icon: <SettingOutlined />, key: 'organization' },
  { title: 'Сроки и бюджет', icon: <CalendarOutlined />, key: 'timeline' },
  { title: 'Генерация', icon: <ThunderboltOutlined />, key: 'generate' },
];

const defaultAnswers = {
  land_status: null,
  land_purchase_stage: null,
  has_mortgage: false,
  has_cadastral: false,
  has_encumbrances: false,
  vri_matches: true,
  terrain_complex: false,
  has_surveys: false,
  has_access_roads: true,
  needs_demolition: false,
  has_gpzu: false,
  has_guard_zones: false,
  object_type: null,
  building_area_sqm: null,
  total_area_sqm: null,
  floors: null,
  construction_type: null,
  foundation_type: null,
  has_panoramic_glazing: false,
  has_flat_roof: false,
  has_pool: false,
  has_bath: false,
  has_sports_hall: false,
  has_medical_room: false,
  design_stage: null,
  has_architectural: false,
  has_structural: false,
  has_design_project: false,
  needs_expertise: false,
  electricity_type: null,
  gas_type: null,
  water_type: null,
  sewage_type: null,
  heating_type: null,
  has_smart_home: false,
  has_ventilation: false,
  has_heat_pump: false,
  has_tu: false,
  has_long_delivery: false,
  wall_material: null,
  roof_material: null,
  has_storage: true,
  logistics_responsible: null,
  organization_type: null,
  needs_special_equipment: false,
  has_worker_housing: false,
  target_date: null,
  has_intermediate_goals: false,
  winter_construction: false,
  reserve_days: 14,
  season_config_id: null,
  buildings_count: 1,
  buildings: null,
  cranes_count: 1,
  brigades_count: 1,
  masonry_productivity: 3.0,
  monolith_productivity: 5.0,
};

export default function KSGWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [answers, setAnswers] = useState(defaultAnswers);
  const [projectName, setProjectName] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);

  const updateAnswers = (values) => {
    setAnswers(prev => ({ ...prev, ...values }));
  };

  const next = () => setCurrentStep(currentStep + 1);
  const prev = () => setCurrentStep(currentStep - 1);

  const handleGenerate = async () => {
    if (!projectName.trim()) {
      message.error('Укажите название проекта');
      return;
    }
    if (!startDate) {
      message.error('Укажите дату начала проекта');
      return;
    }

    setGenerating(true);
    try {
      const res = await KSGService.wizardGenerate({
        name: projectName,
        answers: answers,
        project_start_date: startDate.format('YYYY-MM-DD'),
      });
      message.success(`КСГ сгенерирована! Создано задач: ${res.data.tasks_count}`);
      navigate(`/ksg/drafts/${res.data.id}`);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Ошибка генерации');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!startDate) {
      message.error('Укажите дату начала');
      return;
    }
    try {
      const res = await KSGService.wizardPreview({
        name: projectName || 'Предпросмотр',
        answers: answers,
        project_start_date: startDate.format('YYYY-MM-DD'),
      });
      setPreview(res.data);
    } catch {
      message.error('Ошибка предпросмотра');
    }
  };

  const renderLandStep = () => (
    <div>
      <Title level={4}>Земельный участок</Title>
      <Paragraph type="secondary">Ответьте на вопросы о вашем земельном участке</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Статус земельного участка" required>
          <Radio.Group
            value={answers.land_status}
            onChange={(e) => updateAnswers({ land_status: e.target.value })}
          >
            <Radio.Button value="owned">Уже в собственности</Radio.Button>
            <Radio.Button value="purchase">Предстоит покупка</Radio.Button>
            <Radio.Button value="lease">Долгосрочная аренда</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {answers.land_status === 'purchase' && (
          <>
            <Form.Item label="Стадия приобретения">
              <Select
                value={answers.land_purchase_stage}
                onChange={(v) => updateAnswers({ land_purchase_stage: v })}
                placeholder="Выберите стадию"
                options={[
                  { value: 'search', label: 'Поиск участка' },
                  { value: 'checking', label: 'Проверка документов' },
                  { value: 'deal_prep', label: 'Подготовка к сделке' },
                ]}
              />
            </Form.Item>

            <Form.Item label="Будет ли использоваться ипотека?">
              <Switch
                checked={answers.has_mortgage}
                onChange={(v) => updateAnswers({ has_mortgage: v })}
                checkedChildren="Да"
                unCheckedChildren="Нет"
              />
            </Form.Item>
          </>
        )}

        <Form.Item label="Проведено ли межевание и стоит ли участок на кадастровом учёте?">
          <Switch
            checked={answers.has_cadastral}
            onChange={(v) => updateAnswers({ has_cadastral: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Есть ли обременения, сервитуты или аресты на участок?">
          <Switch
            checked={answers.has_encumbrances}
            onChange={(v) => updateAnswers({ has_encumbrances: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Соответствует ли ВРИ участка планам по строительству?">
          <Switch
            checked={answers.vri_matches}
            onChange={(v) => updateAnswers({ vri_matches: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Каков рельеф участка? Потребуются ли масштабные земляные работы?">
          <Switch
            checked={answers.terrain_complex}
            onChange={(v) => updateAnswers({ terrain_complex: v })}
            checkedChildren="Сложный"
            unCheckedChildren="Ровный"
          />
        </Form.Item>

        <Form.Item label="Выполнены ли инженерно-геологические и геодезические изыскания?">
          <Switch
            checked={answers.has_surveys}
            onChange={(v) => updateAnswers({ has_surveys: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Есть ли круглогодичные подъездные пути для тяжёлой техники?">
          <Switch
            checked={answers.has_access_roads}
            onChange={(v) => updateAnswers({ has_access_roads: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Требуется ли демонтаж старых строений или вырубка деревьев?">
          <Switch
            checked={answers.needs_demolition}
            onChange={(v) => updateAnswers({ needs_demolition: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Получен ли ГПЗУ (градостроительный план земельного участка)?">
          <Switch
            checked={answers.has_gpzu}
            onChange={(v) => updateAnswers({ has_gpzu: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Попадает ли участок в охранные зоны (ЛЭП, газопровод, водоохранная)?">
          <Switch
            checked={answers.has_guard_zones}
            onChange={(v) => updateAnswers({ has_guard_zones: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderObjectStep = () => (
    <div>
      <Title level={4}>Параметры объекта</Title>
      <Paragraph type="secondary">Опишите планируемый объект строительства</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Тип объекта" required>
          <Select
            value={answers.object_type}
            onChange={(v) => updateAnswers({ object_type: v })}
            placeholder="Выберите тип"
            options={[
              { value: 'residential', label: 'Жилой дом (ИЖС)' },
              { value: 'multi_residential', label: 'Многоквартирный дом' },
              { value: 'townhouse', label: 'Таунхаус' },
              { value: 'office', label: 'Офис' },
              { value: 'warehouse', label: 'Склад' },
              { value: 'shopping', label: 'Торговый центр' },
              { value: 'production', label: 'Производство' },
              { value: 'social', label: 'Социальный объект' },
              { value: 'sports', label: 'Спортивный объект' },
              { value: 'rehabilitation', label: 'Реабилитационный центр' },
              { value: 'other', label: 'Другое' },
            ]}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Площадь застройки, кв.м">
              <InputNumber
                style={{ width: '100%' }}
                value={answers.building_area_sqm}
                onChange={(v) => updateAnswers({ building_area_sqm: v })}
                min={1}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Общая площадь, кв.м">
              <InputNumber
                style={{ width: '100%' }}
                value={answers.total_area_sqm}
                onChange={(v) => updateAnswers({ total_area_sqm: v })}
                min={1}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Этажность">
              <InputNumber
                style={{ width: '100%' }}
                value={answers.floors}
                onChange={(v) => updateAnswers({ floors: v })}
                min={1}
                max={50}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Конструктивная схема">
          <Select
            value={answers.construction_type}
            onChange={(v) => updateAnswers({ construction_type: v })}
            placeholder="Выберите тип"
            allowClear
            options={[
              { value: 'monolith', label: 'Монолитный каркас' },
              { value: 'brick', label: 'Кирпич' },
              { value: 'block', label: 'Газо/пенобетонные блоки' },
              { value: 'frame', label: 'Каркасная (ЛМК/дерево)' },
              { value: 'panel', label: 'Панельная' },
              { value: 'combined', label: 'Комбинированная' },
            ]}
          />
        </Form.Item>

        <Form.Item label="Тип фундамента">
          <Select
            value={answers.foundation_type}
            onChange={(v) => updateAnswers({ foundation_type: v })}
            placeholder="Выберите тип"
            allowClear
            options={[
              { value: 'strip', label: 'Ленточный' },
              { value: 'slab', label: 'Плитный' },
              { value: 'pile', label: 'Свайный' },
              { value: 'pile_grillage', label: 'Свайно-ростверковый' },
            ]}
          />
        </Form.Item>

        <Divider orientation="left">Нестандартные решения</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Панорамное остекление">
              <Switch checked={answers.has_panoramic_glazing} onChange={(v) => updateAnswers({ has_panoramic_glazing: v })} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Плоская кровля">
              <Switch checked={answers.has_flat_roof} onChange={(v) => updateAnswers({ has_flat_roof: v })} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Специфичные помещения</Divider>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Бассейн">
              <Switch checked={answers.has_pool} onChange={(v) => updateAnswers({ has_pool: v })} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Баня / сауна">
              <Switch checked={answers.has_bath} onChange={(v) => updateAnswers({ has_bath: v })} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Спортзал">
              <Switch checked={answers.has_sports_hall} onChange={(v) => updateAnswers({ has_sports_hall: v })} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Медблок">
              <Switch checked={answers.has_medical_room} onChange={(v) => updateAnswers({ has_medical_room: v })} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );

  const renderDesignStep = () => (
    <div>
      <Title level={4}>Проектная документация</Title>
      <Paragraph type="secondary">На какой стадии находится проектирование?</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Стадия проектирования">
          <Select
            value={answers.design_stage}
            onChange={(v) => updateAnswers({ design_stage: v })}
            placeholder="Выберите стадию"
            allowClear
            options={[
              { value: 'not_started', label: 'Ещё не начато' },
              { value: 'concept', label: 'Эскизный проект' },
              { value: 'project_doc', label: 'Проектная документация' },
              { value: 'working_doc', label: 'Рабочая документация' },
              { value: 'complete', label: 'Проект полностью готов' },
            ]}
          />
        </Form.Item>

        <Divider orientation="left">Готовность разделов</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Архитектурный раздел (АР)">
              <Switch checked={answers.has_architectural} onChange={(v) => updateAnswers({ has_architectural: v })} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Конструктивный раздел (КР)">
              <Switch checked={answers.has_structural} onChange={(v) => updateAnswers({ has_structural: v })} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Дизайн-проект интерьера">
              <Switch checked={answers.has_design_project} onChange={(v) => updateAnswers({ has_design_project: v })} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Требуется ли государственная экспертиза проектной документации?">
          <Switch
            checked={answers.needs_expertise}
            onChange={(v) => updateAnswers({ needs_expertise: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderEngineeringStep = () => (
    <div>
      <Title level={4}>Инженерные сети</Title>
      <Paragraph type="secondary">Укажите тип подключения для каждой системы</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Электроснабжение">
          <Radio.Group value={answers.electricity_type} onChange={(e) => updateAnswers({ electricity_type: e.target.value })}>
            <Radio value="central">Центральное</Radio>
            <Radio value="autonomous">Автономное</Radio>
            <Radio value="none">Нет</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="Газоснабжение">
          <Radio.Group value={answers.gas_type} onChange={(e) => updateAnswers({ gas_type: e.target.value })}>
            <Radio value="central">Центральное</Radio>
            <Radio value="autonomous">Газгольдер</Radio>
            <Radio value="none">Нет</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="Водоснабжение">
          <Radio.Group value={answers.water_type} onChange={(e) => updateAnswers({ water_type: e.target.value })}>
            <Radio value="central">Центральное</Radio>
            <Radio value="well">Скважина</Radio>
            <Radio value="none">Нет</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="Канализация">
          <Radio.Group value={answers.sewage_type} onChange={(e) => updateAnswers({ sewage_type: e.target.value })}>
            <Radio value="central">Центральная</Radio>
            <Radio value="autonomous">Септик</Radio>
            <Radio value="none">Нет</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="Теплоснабжение">
          <Radio.Group value={answers.heating_type} onChange={(e) => updateAnswers({ heating_type: e.target.value })}>
            <Radio value="central">Центральное</Radio>
            <Radio value="gas_boiler">Газовый котёл</Radio>
            <Radio value="electric">Электрическое</Radio>
            <Radio value="heat_pump">Тепловой насос</Radio>
          </Radio.Group>
        </Form.Item>

        <Divider orientation="left">Дополнительные системы</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Умный дом">
              <Switch checked={answers.has_smart_home} onChange={(v) => updateAnswers({ has_smart_home: v })} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Приточно-вытяжная вентиляция">
              <Switch checked={answers.has_ventilation} onChange={(v) => updateAnswers({ has_ventilation: v })} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Тепловой насос">
              <Switch checked={answers.has_heat_pump} onChange={(v) => updateAnswers({ has_heat_pump: v })} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Получены ли технические условия (ТУ) на подключение?">
          <Switch
            checked={answers.has_tu}
            onChange={(v) => updateAnswers({ has_tu: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderMaterialsStep = () => (
    <div>
      <Title level={4}>Материалы и логистика</Title>
      <Paragraph type="secondary">Основные строительные и отделочные материалы</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Основной стеновой материал">
          <Select
            value={answers.wall_material}
            onChange={(v) => updateAnswers({ wall_material: v })}
            placeholder="Выберите"
            allowClear
            options={[
              { value: 'brick', label: 'Кирпич' },
              { value: 'gas_block', label: 'Газобетонный блок' },
              { value: 'monolith', label: 'Монолит' },
              { value: 'frame_wood', label: 'Каркас (дерево)' },
              { value: 'frame_steel', label: 'Каркас (ЛМК)' },
              { value: 'sip', label: 'СИП-панели' },
              { value: 'other', label: 'Другое' },
            ]}
          />
        </Form.Item>

        <Form.Item label="Кровельный материал">
          <Select
            value={answers.roof_material}
            onChange={(v) => updateAnswers({ roof_material: v })}
            placeholder="Выберите"
            allowClear
            options={[
              { value: 'metal_tile', label: 'Металлочерепица' },
              { value: 'soft_tile', label: 'Гибкая черепица' },
              { value: 'natural_tile', label: 'Натуральная черепица' },
              { value: 'profiled_sheet', label: 'Профнастил' },
              { value: 'flat_roof_membrane', label: 'Мембрана (плоская кровля)' },
              { value: 'other', label: 'Другое' },
            ]}
          />
        </Form.Item>

        <Form.Item label="Есть ли материалы с длительным сроком поставки под заказ?">
          <Switch
            checked={answers.has_long_delivery}
            onChange={(v) => updateAnswers({ has_long_delivery: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Есть ли возможность складирования материалов на площадке?">
          <Switch
            checked={answers.has_storage}
            onChange={(v) => updateAnswers({ has_storage: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Кто несёт ответственность за логистику и закупку материалов?">
          <Select
            value={answers.logistics_responsible}
            onChange={(v) => updateAnswers({ logistics_responsible: v })}
            placeholder="Выберите"
            allowClear
            options={[
              { value: 'general_contractor', label: 'Генподрядчик' },
              { value: 'customer', label: 'Заказчик' },
              { value: 'shared', label: 'Совместно' },
            ]}
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderOrganizationStep = () => (
    <div>
      <Title level={4}>Организация строительства</Title>
      <Paragraph type="secondary">Как будет организовано строительство?</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Кто будет вести строительство?">
          <Select
            value={answers.organization_type}
            onChange={(v) => updateAnswers({ organization_type: v })}
            placeholder="Выберите"
            allowClear
            options={[
              { value: 'general_contractor', label: 'Один генподрядчик под ключ' },
              { value: 'separate_brigrades', label: 'Несколько бригад на каждый этап' },
              { value: 'self_build', label: 'Самостоятельное строительство' },
              { value: 'mixed', label: 'Смешанный вариант' },
            ]}
          />
        </Form.Item>

        <Divider orientation="left">Ресурсы и скорость</Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Количество зданий на участке">
              <InputNumber
                value={answers.buildings_count}
                onChange={(v) => updateAnswers({ buildings_count: v || 1 })}
                min={1}
                max={50}
                style={{ width: '100%' }}
                addonAfter="зданий"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Количество бригад">
              <InputNumber
                value={answers.brigades_count}
                onChange={(v) => updateAnswers({ brigades_count: v || 1 })}
                min={1}
                max={20}
                style={{ width: '100%' }}
                addonAfter="бригад"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Количество кранов">
              <InputNumber
                value={answers.cranes_count}
                onChange={(v) => updateAnswers({ cranes_count: v || 1 })}
                min={0}
                max={10}
                style={{ width: '100%' }}
                addonAfter="кранов"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Производительность кладки">
              <InputNumber
                value={answers.masonry_productivity}
                onChange={(v) => updateAnswers({ masonry_productivity: v || 3 })}
                min={0.5}
                max={20}
                step={0.5}
                style={{ width: '100%' }}
                addonAfter="м³/день на бригаду"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Производительность монолита">
              <InputNumber
                value={answers.monolith_productivity}
                onChange={(v) => updateAnswers({ monolith_productivity: v || 5 })}
                min={0.5}
                max={30}
                step={0.5}
                style={{ width: '100%' }}
                addonAfter="м³/день на бригаду"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Form.Item label="Требуется ли бронирование дефицитной спецтехники?">
          <Switch
            checked={answers.needs_special_equipment}
            onChange={(v) => updateAnswers({ needs_special_equipment: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Требуется ли организация быта рабочих (проживание на площадке)?">
          <Switch
            checked={answers.has_worker_housing}
            onChange={(v) => updateAnswers({ has_worker_housing: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderTimelineStep = () => (
    <div>
      <Title level={4}>Сроки и бюджет</Title>
      <Paragraph type="secondary">Целевые сроки и особенности графика</Paragraph>
      <Divider />

      <Form layout="vertical">
        <Form.Item label="Название проекта" required>
          <Input
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Например: Жилой дом на ул. Ленина"
          />
        </Form.Item>

        <Form.Item label="Планируемая дата начала строительства" required>
          <DatePicker
            value={startDate}
            onChange={setStartDate}
            style={{ width: '100%' }}
            placeholder="Выберите дату"
          />
        </Form.Item>

        <Form.Item label="Целевая дата завершения">
          <DatePicker
            value={answers.target_date ? dayjs(answers.target_date) : null}
            onChange={(v) => updateAnswers({ target_date: v?.format('YYYY-MM-DD') || null })}
            style={{ width: '100%' }}
            placeholder="Выберите дату"
          />
        </Form.Item>

        <Form.Item label="Есть ли промежуточные цели (например, закрыть тепловой контур до зимы)?">
          <Switch
            checked={answers.has_intermediate_goals}
            onChange={(v) => updateAnswers({ has_intermediate_goals: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
        </Form.Item>

        <Form.Item label="Планируется ли строительство в зимний период?">
          <Switch
            checked={answers.winter_construction}
            onChange={(v) => updateAnswers({ winter_construction: v })}
            checkedChildren="Да"
            unCheckedChildren="Нет"
          />
          {answers.winter_construction && (
            <Text type="warning" style={{ display: 'block', marginTop: 8 }}>
              Будут добавлены дополнительные задачи на прогрев бетона, спецдобавки и укрытие конструкций
            </Text>
          )}
        </Form.Item>

        <Form.Item label="Резервные дни на форс-мажоры">
          <InputNumber
            value={answers.reserve_days}
            onChange={(v) => updateAnswers({ reserve_days: v })}
            min={0}
            max={90}
            addonAfter="дней"
            style={{ width: 200 }}
          />
        </Form.Item>
      </Form>
    </div>
  );

  const renderGenerateStep = () => (
    <div>
      <Title level={4}>Генерация КСГ</Title>
      <Paragraph type="secondary">Проверьте данные и запустите генерацию</Paragraph>
      <Divider />

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col span={8}><Text type="secondary">Название:</Text></Col>
          <Col span={16}><Text strong>{projectName || '—'}</Text></Col>

          <Col span={8}><Text type="secondary">Дата начала:</Text></Col>
          <Col span={16}><Text strong>{startDate ? startDate.format('DD.MM.YYYY') : '—'}</Text></Col>

          <Col span={8}><Text type="secondary">Земельный участок:</Text></Col>
          <Col span={16}>
            <Tag color={answers.land_status === 'owned' ? 'green' : answers.land_status === 'purchase' ? 'orange' : 'blue'}>
              {answers.land_status === 'owned' ? 'В собственности' : answers.land_status === 'purchase' ? 'Покупка' : answers.land_status === 'lease' ? 'Аренда' : '—'}
            </Tag>
          </Col>

          <Col span={8}><Text type="secondary">Тип объекта:</Text></Col>
          <Col span={16}><Text>{answers.object_type || '—'}</Text></Col>

          <Col span={8}><Text type="secondary">Площадь:</Text></Col>
          <Col span={16}><Text>{answers.total_area_sqm ? `${answers.total_area_sqm} кв.м` : '—'}</Text></Col>

          <Col span={8}><Text type="secondary">Этажность:</Text></Col>
          <Col span={16}><Text>{answers.floors || '—'}</Text></Col>

          <Col span={8}><Text type="secondary">Зимнее строительство:</Text></Col>
          <Col span={16}>
            <Tag color={answers.winter_construction ? 'red' : 'green'}>
              {answers.winter_construction ? 'Да' : 'Нет'}
            </Tag>
          </Col>
        </Row>
      </Card>

      {preview && (
        <Card style={{ marginBottom: 16, background: '#f6ffed' }}>
          <Row gutter={[16, 8]}>
            <Col span={8}><Text type="secondary">Блоков задач:</Text></Col>
            <Col span={16}><Text strong>{preview.blocks_count}</Text></Col>
            <Col span={8}><Text type="secondary">Всего задач:</Text></Col>
            <Col span={16}><Text strong>{preview.tasks_count}</Text></Col>
            <Col span={8}><Text type="secondary">Общая стоимость:</Text></Col>
            <Col span={16}><Text strong>{preview.total_cost?.toLocaleString()} ₽</Text></Col>
            <Col span={8}><Text type="secondary">Длительность:</Text></Col>
            <Col span={16}><Text strong>{preview.total_duration_days} дней</Text></Col>
          </Row>
        </Card>
      )}

      <Space>
        <Button onClick={handlePreview} loading={generating}>
          Предпросмотр
        </Button>
        <Button
          type="primary"
          size="large"
          icon={<ThunderboltOutlined />}
          onClick={handleGenerate}
          loading={generating}
          style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none' }}
        >
          Сгенерировать КСГ
        </Button>
      </Space>
    </div>
  );

  const stepRenderers = [
    renderLandStep,
    renderObjectStep,
    renderDesignStep,
    renderEngineeringStep,
    renderMaterialsStep,
    renderOrganizationStep,
    renderTimelineStep,
    renderGenerateStep,
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Создание КСГ</h1>
        <p className="page-subtitle">Пошаговый визард генерации комплексной стандартной группы</p>
      </div>

      <Card style={{ marginBottom: 24, borderRadius: 16 }}>
        <Steps
          current={currentStep}
          items={STEPS.map(s => ({ title: s.title, icon: s.icon }))}
          size="small"
          responsive
        />
      </Card>

      <Card style={{ borderRadius: 16, minHeight: 400 }}>
        {stepRenderers[currentStep]()}
      </Card>

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={prev}
          disabled={currentStep === 0}
        >
          Назад
        </Button>

        <Space>
          <Button onClick={() => navigate('/ksg')}>
            Отмена
          </Button>
          {currentStep < STEPS.length - 1 && (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={next}
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', border: 'none' }}
            >
              Далее
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}
